/**
 * Agent A2 — Rule Drafting (V6-1-10 / ADR-0034, upgraded V85-4-6 / gap B11).
 *
 * Given the text of a legislative instrument and its identifier, drafts a
 * jurisdiction rule in the ADR-0024 shape. The draft is always a proposal
 * (it enters via proposed_changes with target_type='jurisdiction_rule');
 * this service only produces the candidate — it never writes to the DB.
 *
 * V85-4-6 replaces the stub with:
 *   1. LLM-based drafting via IAIProvider (when provided)
 *   2. Predicate validation against ADR-0024 vocabulary
 *   3. Fixture validation via evaluatePredicate
 *   4. Source provenance retention (links back to DPA finding or scanner)
 *   5. Fallback to stub when no provider is available
 *
 * All drafts go through proposed_changes queue with
 * queue_target='proposed_changes'. Nothing activates without editor
 * approval. Uses the V85-4-0 harness.
 */
import { defineTemplate, field } from '../egress/data-classes.js';
import { BOOLEAN_FIELDS, SCALAR_FIELDS, ARRAY_FIELDS, PREDICATE_OPERATORS, CANONICAL_ROLES, RULE_TIERS, canonicalScope, isRuleTier, } from './jurisdiction-applicability-service.js';
import { collectPredicateErrors, validateTestFixtures, } from './jurisdiction-rule-service.js';
// ---- Agent definition -------------------------------------------------------
export const A2_DEFINITION = {
    agent_id: 'A2',
    // 2026.10.09: ADR-0077 — canonical roles/tiers only; "both" is no longer offered.
    prompt_version: '2026.10.09',
    queue_target: 'proposed_changes',
    schema_version: '1.0',
};
export const A2_OUTPUT_SCHEMA = {
    type: 'object',
    required: ['jurisdiction', 'statute', 'citation', 'predicates', 'obligations', 'effective_date', 'fixtures', 'rationale'],
    properties: {
        jurisdiction: { type: 'string' },
        statute: { type: 'string' },
        citation: { type: 'string' },
        predicates: { type: 'object' },
        obligations: { type: 'array' },
        effective_date: { type: 'string' },
        fixtures: { type: 'array' },
        rationale: { type: 'string' },
    },
};
// ---- Validation helpers -----------------------------------------------------
/**
 * Validate a drafted predicate against the ADR-0024 vocabulary.
 * Returns error list (empty = valid).
 */
export function validateDraftedPredicate(predicate) {
    return collectPredicateErrors(predicate);
}
/**
 * Validate drafted fixtures against the predicate.
 * Returns error list (empty = valid).
 */
export function validateDraftedFixtures(predicate, fixtures) {
    const asRuleFixtures = fixtures.map((f) => ({
        label: f.description,
        classification: f.input,
        expected: f.expected_match,
    }));
    return validateTestFixtures(predicate, asRuleFixtures);
}
// ---- Stub fallback ----------------------------------------------------------
/**
 * Stub drafting: returns placeholder values that pass shape validation
 * but are clearly marked as stubs needing editor review. This is the
 * backward-compatible fallback when no IAIProvider is available.
 */
export function stubDraft(instrumentId) {
    return {
        jurisdiction: '',
        statute: '',
        citation: '',
        predicates: { eq: { field: 'consequential_decision', value: true } },
        obligations: [],
        effective_date: new Date().toISOString().slice(0, 10),
        fixtures: [
            {
                description: 'Positive fixture placeholder',
                input: { consequential_decision: true },
                expected_match: true,
            },
            {
                description: 'Negative fixture placeholder',
                input: { consequential_decision: false },
                expected_match: false,
            },
        ],
        rationale: 'stub',
    };
}
// ---- Legacy stub (backward compat) ------------------------------------------
/**
 * Draft a jurisdiction rule from a legislative instrument's text.
 *
 * This is the legacy stub interface used by dpa-backfill-service. It
 * remains as the no-provider fallback. For the full agent path, use
 * `ruleDraftingAgent` via `runAgent()`.
 */
export async function draftRuleFromInstrument(_text, _instrumentId) {
    if (!_instrumentId || _instrumentId.trim().length === 0) {
        throw new Error('instrumentId is required');
    }
    return stubDraft(_instrumentId);
}
// ---- LLM-based drafting -----------------------------------------------------
/**
 * Draft a rule through the platform egress gateway. Validates the output against the
 * ADR-0024 vocabulary and fixture gate. Falls back to stub on any
 * validation failure.
 */
export async function draftRuleWithProvider(llm, input) {
    if (!input.instrument_id || input.instrument_id.trim().length === 0) {
        throw new Error('instrument_id is required');
    }
    let raw;
    try {
        // ADR-0073: platform gateway (public legislation). A refusal falls back to the stub.
        const result = await llm.run({
            task: 'agent.a2.rule_draft', template: A2_TEMPLATE, slots: draftingSlots(input),
            schemaHint: A2_OUTPUT_SCHEMA,
        });
        if (result.status !== 'ok' || !result.output) {
            return stubDraft(input.instrument_id);
        }
        raw = result.output;
    }
    catch {
        // LLM failure: fall back to stub
        return stubDraft(input.instrument_id);
    }
    // Validate predicate against ADR-0024 vocabulary
    const predicateErrors = validateDraftedPredicate(raw.predicates);
    if (predicateErrors.length > 0) {
        // Invalid predicate — fall back to stub with LLM rationale
        const stub = stubDraft(input.instrument_id);
        stub.rationale = `LLM draft had invalid predicate (${predicateErrors.join('; ')}). ` +
            `LLM rationale preserved: ${raw.rationale}`;
        stub.jurisdiction = raw.jurisdiction || input.jurisdiction || '';
        stub.statute = raw.statute || input.title || '';
        stub.citation = raw.citation || input.source_url || '';
        stub.source_url = input.source_url;
        stub.instrument_version = input.instrument_version;
        return stub;
    }
    // Build fixtures and validate
    const fixtures = (raw.fixtures ?? []).length > 0
        ? raw.fixtures
        : [
            {
                description: 'Auto-generated positive fixture',
                input: { consequential_decision: true },
                expected_match: true,
            },
            {
                description: 'Auto-generated negative fixture',
                input: { consequential_decision: false },
                expected_match: false,
            },
        ];
    const fixtureErrors = validateDraftedFixtures(raw.predicates, fixtures);
    if (fixtureErrors.length > 0) {
        // Fixtures don't pass — fall back to stub predicate with LLM context
        const stub = stubDraft(input.instrument_id);
        stub.rationale = `LLM draft had fixture validation failures (${fixtureErrors.join('; ')}). ` +
            `LLM rationale preserved: ${raw.rationale}`;
        stub.jurisdiction = raw.jurisdiction || input.jurisdiction || '';
        stub.statute = raw.statute || input.title || '';
        stub.citation = raw.citation || input.source_url || '';
        stub.obligations = raw.obligations ?? [];
        stub.source_url = input.source_url;
        stub.instrument_version = input.instrument_version;
        return stub;
    }
    return {
        jurisdiction: raw.jurisdiction || input.jurisdiction || '',
        statute: raw.statute || input.title || '',
        citation: raw.citation || input.source_url || '',
        predicates: raw.predicates,
        obligations: canonicalLabels(raw.obligations ?? []),
        effective_date: raw.effective_date || input.published_at?.slice(0, 10) || new Date().toISOString().slice(0, 10),
        fixtures,
        rationale: raw.rationale || 'LLM-drafted rule',
        source_url: input.source_url,
        instrument_version: input.instrument_version,
    };
}
// ---- Agent function (harness-compatible) ------------------------------------
/**
 * A2 agent function for use with runAgent(). Drafts a jurisdiction rule
 * from legislative instrument text, validates against ADR-0024, and
 * returns a proposal for the proposed_changes queue.
 */
export const ruleDraftingAgent = async (llm, input) => {
    const data = input.data;
    if (!data.instrument_id) {
        throw new Error('instrument_id is required');
    }
    if (!data.instrument_text) {
        throw new Error('instrument_text is required');
    }
    const draft = await draftRuleWithProvider(llm, data);
    const provenance = {
        jurisdiction: {
            source: data.source_url ?? data.instrument_id,
            span: draft.jurisdiction,
            confidence: draft.rationale === 'stub' ? 0.1 : 0.7,
        },
        statute: {
            source: data.source_url ?? data.instrument_id,
            span: draft.statute,
            confidence: draft.rationale === 'stub' ? 0.1 : 0.8,
        },
        citation: {
            source: data.source_url ?? data.instrument_id,
            span: draft.citation,
            confidence: draft.rationale === 'stub' ? 0.1 : 0.8,
        },
        predicates: {
            source: data.source_url ?? data.instrument_id,
            confidence: draft.rationale === 'stub' ? 0.1 : 0.6,
        },
        obligations: {
            source: data.source_url ?? data.instrument_id,
            span: `${draft.obligations.length} obligation(s)`,
            confidence: draft.rationale === 'stub' ? 0.1 : 0.6,
        },
        effective_date: {
            source: data.source_url ?? data.instrument_id,
            span: draft.effective_date,
            confidence: data.published_at ? 0.9 : 0.5,
        },
        fixtures: {
            source: 'generated',
            span: `${draft.fixtures.length} fixture(s)`,
            confidence: draft.rationale === 'stub' ? 0.1 : 0.8,
        },
        rationale: {
            source: 'agent-A2',
            span: draft.rationale.slice(0, 100),
            confidence: 1.0,
        },
    };
    const output = {
        target: 'proposed_changes',
        proposal: {
            jurisdiction: draft.jurisdiction,
            statute: draft.statute,
            citation: draft.citation,
            predicates: draft.predicates,
            obligations: draft.obligations,
            effective_date: draft.effective_date,
            fixtures: draft.fixtures,
            rationale: draft.rationale,
            source_url: draft.source_url,
            instrument_version: draft.instrument_version,
        },
        provenance,
    };
    return output;
};
/**
 * ADR-0077 §5: a drafted obligation keeps canonical labels only. "both" in
 * `scope` becomes provider + deployer; other non-canonical roles and tiers
 * are dropped, so the draft never fails validation on a label.
 */
function canonicalLabels(obligations) {
    return obligations.map((o) => {
        const { scope: rawScope, tier: rawTier, ...rest } = o ?? {};
        const scope = canonicalScope(rawScope);
        return { ...rest, ...(isRuleTier(rawTier) ? { tier: rawTier } : {}), ...(scope ? { scope } : {}) };
    });
}
// ---- Prompt construction ----------------------------------------------------
/**
 * ADR-0073: the A2 prompt as a reviewed template. Platform scope: the
 * instrument is public legislation and the vocabulary is GAC Pro-authored
 * (reference); the gateway throws if anything else reaches this call (R2).
 * (The pre-gateway builder dropped blank lines; the template keeps that shape.)
 */
const A2_PROMPT = [
    'You are an AI regulatory analyst. Draft a jurisdiction rule from the following legislative instrument.',
    '## ADR-0024 Vocabulary (frozen — use ONLY these fields and operators)',
    '{vocabulary}',
    '## Predicate format',
    'A predicate is a nested object with exactly one operator key.',
    'Examples:',
    '  { "eq": { "field": "consequential_decision", "value": true } }',
    '  { "and": [{ "eq": { "field": "employment_decision", "value": true } }, { "contains": { "field": "deployment_jurisdictions", "value": "US-CO" } }] }',
    '## Obligation format',
    'Each obligation: { "obligation_id": "unique-slug", "title": "...", "duty": "...", "citation": "specific section", "deadline_rule": { "kind": "fixed"|"relative"|"none" }, "tier": {canonical_tiers} (optional), "scope": [one or more of {canonical_roles}] (optional; list every role the duty falls on) }',
    '## Fixture requirements',
    'Include at least 1 positive fixture (expected_match: true) and 1 negative fixture (expected_match: false).',
    'Each fixture\'s `input` must evaluate correctly against your predicate.',
    '## Source information',
    '{source_info}',
    '## Instrument text (truncated to 8000 chars)',
    '{instrument_text}',
    'Return JSON: { "jurisdiction", "statute", "citation", "predicates", "obligations", "effective_date", "fixtures": [{ "description", "input", "expected_match" }], "rationale" }',
].join('\n');
const A2_TEMPLATE = defineTemplate('agent.a2.rule_draft', '2026.10.09', A2_PROMPT, ['vocabulary', 'canonical_tiers', 'canonical_roles', 'source_info', 'instrument_text']);
function draftingSlots(input) {
    const vocabLines = [
        `Boolean fields: ${BOOLEAN_FIELDS.join(', ')}`,
        `Scalar fields: ${SCALAR_FIELDS.join(', ')}`,
        `Array fields: ${ARRAY_FIELDS.join(', ')}`,
        `Predicate operators: ${PREDICATE_OPERATORS.join(', ')}`,
    ];
    const sourceLines = [
        input.jurisdiction ? `Jurisdiction: ${input.jurisdiction}` : '',
        input.title ? `Title: ${input.title}` : '',
        input.source_url ? `URL: ${input.source_url}` : '',
        input.published_at ? `Published: ${input.published_at}` : '',
    ].filter(Boolean);
    return {
        vocabulary: field('rules.vocabulary', vocabLines.join('\n')),
        // ADR-0077: canonical tiers and roles only ("both" is no longer offered).
        canonical_tiers: field('rules.vocabulary', RULE_TIERS.map((t) => `"${t}"`).join('|')),
        canonical_roles: field('rules.vocabulary', CANONICAL_ROLES.map((r) => `"${r}"`).join(', ')),
        source_info: field('research.legislation_text', sourceLines.join('\n')),
        instrument_text: field('research.legislation_text', input.instrument_text.slice(0, 8000)),
    };
}
//# sourceMappingURL=agent-rule-drafting-service.js.map